/**
 * Shared i18n resources for FrontDesk+ applications
 *
 * This module exports translation resources that can be used by any app.
 * Each app is responsible for initializing i18next with these resources.
 *
 * @example
 * // In your app's i18n setup
 * import { resources, SupportedLanguage } from '@ascleo/frontdesk-shared/i18n'
 * import i18n from 'i18next'
 * import { initReactI18next } from 'react-i18next'
 *
 * i18n.use(initReactI18next).init({
 *   resources,
 *   lng: 'de',
 *   fallbackLng: 'de',
 *   interpolation: { escapeValue: false }
 * })
 */

import { germanFrontDeskTranslation as de } from './de'
import { englishFrontDeskTranslation as en } from './en'
import {
  FRONTDESK_LANGUAGE_CATALOG,
  type FrontDeskLanguageCode,
} from './language-catalog'
import tr from './tr.json'
import ru from './ru.json'
import uk from './uk.json'
import ar from './ar.json'
import fa from './fa.json'
import ps from './ps.json'
import fr from './fr.json'
import es from './es.json'
import it from './it.json'
import pl from './pl.json'
import ro from './ro.json'
import bg from './bg.json'
import nl from './nl.json'
import pt from './pt.json'
import hr from './hr.json'
import cs from './cs.json'
import sk from './sk.json'
import hu from './hu.json'
import sr from './sr.json'
import ko from './ko.json'
import zh from './zh.json'
import ja from './ja.json'

/**
 * i18next resources object ready to be used in app initialization
 */
export const resources = {
  de: { translation: de },
  en: { translation: en },
  tr: { translation: tr },
  ru: { translation: ru },
  uk: { translation: uk },
  ar: { translation: ar },
  fa: { translation: fa },
  ps: { translation: ps },
  fr: { translation: fr },
  es: { translation: es },
  it: { translation: it },
  pl: { translation: pl },
  ro: { translation: ro },
  bg: { translation: bg },
  nl: { translation: nl },
  pt: { translation: pt },
  hr: { translation: hr },
  cs: { translation: cs },
  sk: { translation: sk },
  hu: { translation: hu },
  sr: { translation: sr },
  ko: { translation: ko },
  zh: { translation: zh },
  ja: { translation: ja }
} as const

/**
 * Supported language codes
 */
export type SupportedLanguage = FrontDeskLanguageCode

/**
 * List of all supported language codes
 */
export const supportedLanguages: SupportedLanguage[] =
  FRONTDESK_LANGUAGE_CATALOG.map(({ code }) => code)

/**
 * Default language
 */
export const defaultLanguage: SupportedLanguage = 'de'

/**
 * Default i18next configuration (can be extended by apps)
 */
export const defaultI18nConfig = {
  resources,
  lng: defaultLanguage,
  fallbackLng: defaultLanguage,
  interpolation: {
    escapeValue: false
  }
} as const
